/**
 * Task Explorer Page
 *
 * Browse and explore all available tasks with filtering and search.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useTasks } from '../tasks/useTasks';
import type { TaskListItem } from '../../lib/tasksApi';
import { matchesSearch } from '../../lib/textUtils';

export function TaskExplorerPage() {
  const navigate = useNavigate();
  const { data: tasks, isLoading: isLoadingTasks } = useTasks();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);

  // Filter tasks based on search (accent-insensitive)
  const filteredTasks = useMemo(() => {
    let result = tasks?.data || [];

    if (searchQuery) {
      result = result.filter(
        (task) =>
          matchesSearch(task.name, searchQuery) ||
          matchesSearch(task.beschrijving ?? '', searchQuery)
      );
    }

    return result;
  }, [tasks?.data, searchQuery]);

  if (isLoadingTasks) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Taken laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="task-explorer-page" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug naar dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Taken Verkenner</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verken alle beschikbare taken in het functieloonhuis.
          </p>
        </div>

        {/* Search */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="input-icon-wrapper">
                <svg className="input-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Zoek taken..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Results count */}
            <div className="text-sm text-gray-500">
              {filteredTasks.length} taken gevonden
            </div>
          </div>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-sm font-medium text-gray-900">Geen taken gevonden</h3>
            <p className="mt-2 text-sm text-gray-500">
              Probeer andere zoektermen.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onToggle={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

interface TaskCardProps {
  task: TaskListItem;
  isExpanded: boolean;
  onToggle: () => void;
}

function TaskCard({ task, isExpanded, onToggle }: TaskCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-medium text-gray-900">{task.name}</h3>
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
              {task.weging} pt
            </span>
          </div>
          {task.beschrijving && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-1">{task.beschrijving}</p>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          {task.beschrijving && (
            <div className="mt-4">
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Beschrijving</h4>
              <p className="text-sm text-gray-700">{task.beschrijving}</p>
            </div>
          )}

          {task.competencies && task.competencies.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                Gekoppelde Competenties
              </h4>
              <div className="flex flex-wrap gap-2">
                {task.competencies.map((comp) => (
                  <span
                    key={comp.id}
                    className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded"
                  >
                    {comp.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
            <span>ID: {task.id}</span>
            <span>Weging: {task.weging} punten</span>
            <span>{task.employeesCount} medewerkers toegewezen</span>
            <span className={task.isActief ? 'text-green-600' : 'text-red-600'}>
              {task.isActief ? 'Actief' : 'Inactief'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
